import React, { useEffect } from 'react';

import { DataNotFoundAlert } from 'components/DataNotFoundAlert';
import { GoogleDriveAuthPopupRedirect } from 'components/GoogleDriveAccountHandler/GoogleDriveAuthPopupRedirect';
import { InstagramAuthPopupRedirect } from 'components/InstagramAccountHandler/InstagramAuthPopupRedirect';
import { SideBarNavOutlet } from 'components/SideNavBarOutlet';
import { GoogleAuthHandlerPage } from 'pages/GoogleAuthHandler/GoogleAuthHandlerPage';
import { GoogleDriveViewerPage } from 'pages/GoogleDriveViewer/GoogleDriveViewerPage';
import { GuestSmartAssistantDashboardPage } from 'pages/GuestSmartAssistant/GuestSmartAssistantDashboardPage';
import { GuestSmartAssistantPage } from 'pages/GuestSmartAssistant/GuestSmartAssistantPage';
import { LoginPage } from 'pages/Login/LoginPage';
import { ManageSmartAssistantPageWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantPageWrapper';
import { OnboardingPage } from 'pages/Onboarding/OnboardingPage';
import { OrganisationPage } from 'pages/Organisation/OrganisationPage';
import { PayWallPage } from 'pages/PayWall/PayWallPage';
import { PdfViewerPage } from 'pages/PdfViewer/PdfViewerPage';
import { PostBillingSessionHandlerPage } from 'pages/PostBillingSessionHandler/PostBillingSessionHandlerPage';
import { PostLoginRouterPage } from 'pages/PostLoginRouter/PostLoginRouterPage';
import { ResourcesPage } from 'pages/Resources/ResourcesPage';
import { SelectSmartAssistantTypePage } from 'pages/SelectSmartAssistantType/SelectSmartAssistantTypePage';
import { SettingsPage } from 'pages/Settings/SettingsPage';
import { ShopifyAuthHandlerPage } from 'pages/ShopifyAuthHandler/ShopifyAuthHandlerPage';
import { SignUpPage } from 'pages/SignUp/SignUpPage';
import { SignUpVerificationPage } from 'pages/SignUpVerification/SignUpVerificationPage';
import { SmartAssistantsPage } from 'pages/SmartAssistants/SmartAssistantsPage';
import { SmartEditorPage } from 'pages/SmartEditor/SmartEditorPage';
import { SubscriptionRedirectHandlerPage } from 'pages/SubscriptionRedirectHandler/SubscriptionRedirectHandlerPage';
import { VideoViewerPage } from 'pages/VideoViewer/VideoViewerPage';
import { WebsiteViewerPage } from 'pages/WebsiteViewer/WebsiteViewerPage';
import { YoutubeViewerPage } from 'pages/YoutubeViewer/YoutubeViewerPage';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { UserAccess } from 'shared/interfaces/User';
import { AuthenticatedRouteGuard, UnAuthenticatedRouteGuard } from 'shared/route-guards/AuthRouteGuard';
import { BaseGuards } from 'shared/route-guards/BaseGuards';
import { CustomOrganisationGuard } from 'shared/route-guards/CustomOrganisationExclusive';
import { FreeAccountGuard } from 'shared/route-guards/FreeAccountGuard';
import { RoleRouteGuard } from 'shared/route-guards/RoleRouteGuard';
import { ScreenSizeRouteGuard } from 'shared/route-guards/ScreenSizeRouteGuard';
import { WhiteLabelAccessGuard } from 'shared/route-guards/WhiteLabelAccessGuard';

export function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/') {
      navigate(ROUTE_LINKS.login);
    }
  }, [location, navigate]);

  return (
    <Routes>
      <Route element={<BaseGuards />}>
        <Route
          path={`${ROUTE_LINKS.guestSmartAssistant}/:smartAssistantId`}
          element={<GuestSmartAssistantPage />}></Route>
        <Route
          path={`${ROUTE_LINKS.legacyGuestSmartAssistant}/:smartAssistantId`}
          element={<GuestSmartAssistantPage />}></Route>
        {/* With and without a tab as param */}
        <Route
          path={`${ROUTE_LINKS.guestSmartAssistantDashboard}/:smartAssistantId`}
          element={<GuestSmartAssistantDashboardPage />}></Route>
        <Route
          path={`${ROUTE_LINKS.guestSmartAssistantDashboard}/:smartAssistantId/:tab`}
          element={<GuestSmartAssistantDashboardPage />}></Route>
      </Route>

      <Route element={<BaseGuards />}>
        <Route element={<WhiteLabelAccessGuard />}>
          <Route path={ROUTE_LINKS.shopifyAuthHandler} element={<ShopifyAuthHandlerPage />}></Route>
          <Route element={<AuthenticatedRouteGuard />}>
            <Route path={ROUTE_LINKS.login} element={<LoginPage />}></Route>
            <Route path={ROUTE_LINKS.verifySignUp} element={<SignUpVerificationPage />}></Route>
            <Route path={ROUTE_LINKS.signUp} element={<SignUpPage />}></Route>
            <Route path={ROUTE_LINKS.googleAuth} element={<GoogleAuthHandlerPage />}></Route>
          </Route>

          <Route element={<UnAuthenticatedRouteGuard />}>
            <Route element={<ScreenSizeRouteGuard />}>
              <Route element={<FreeAccountGuard />}>
                <Route path={ROUTE_LINKS.postLoginRouter} element={<PostLoginRouterPage />}></Route>
                <Route path={ROUTE_LINKS.onboarding} element={<OnboardingPage />}></Route>
                <Route
                  path={ROUTE_LINKS.googleDriveAuthRedirectHandler}
                  element={<GoogleDriveAuthPopupRedirect />}></Route>
                <Route
                  path={ROUTE_LINKS.instagramAuthPopupRedirectHandler}
                  element={<InstagramAuthPopupRedirect />}></Route>
                <Route path={ROUTE_LINKS.postBillingSessionHandler} element={<PostBillingSessionHandlerPage />}></Route>
                {/* DASHBOARD */}
                <Route path={ROUTE_LINKS.dashboard} element={<SideBarNavOutlet />}>
                  {/* PayWall page is the only route here that is not protected by the FreeAccountGuard */}
                  <Route path={ROUTE_LINKS.paywall} element={<PayWallPage />}></Route>
                  <Route path={ROUTE_LINKS.smartAssistants} element={<SmartAssistantsPage />}></Route>
                  <Route path={ROUTE_LINKS.resources} element={<ResourcesPage />}></Route>
                  <Route path={ROUTE_LINKS.selectSmartAssistantType} element={<SelectSmartAssistantTypePage />}></Route>
                  <Route
                    path={`${ROUTE_LINKS.manageSmartAssistant}/:smartAssistantId/*`}
                    element={<ManageSmartAssistantPageWrapper />}></Route>

                  {/* CUSTOM ORGANISATION DASHBOARD ROUTES */}
                  <Route element={<CustomOrganisationGuard />}>
                    <Route element={<RoleRouteGuard minAccessRole={UserAccess.ADMIN} />}>
                      <Route path={`${ROUTE_LINKS.organisation}/:tabName`} element={<OrganisationPage />}></Route>
                    </Route>
                  </Route>

                  <Route path={`${ROUTE_LINKS.settings}/:tabName`} element={<SettingsPage />}></Route>
                </Route>

                <Route
                  path={`${ROUTE_LINKS.subscriptionRedirectHandler}`}
                  element={<SubscriptionRedirectHandlerPage />}></Route>
                <Route path={`${ROUTE_LINKS.smartEditor}/:resourceId`} element={<SmartEditorPage />}></Route>
                <Route path={`${ROUTE_LINKS.pdfViewer}/:resourceId`} element={<PdfViewerPage />}></Route>
                <Route path={`${ROUTE_LINKS.videoViewer}/:resourceId`} element={<VideoViewerPage />}></Route>
                <Route path={`${ROUTE_LINKS.websiteViewer}/:resourceId`} element={<WebsiteViewerPage />}></Route>
                <Route path={`${ROUTE_LINKS.youtubeViewer}/:resourceId`} element={<YoutubeViewerPage />}></Route>
                <Route
                  path={`${ROUTE_LINKS.googleDriveViewer}/:resourceId`}
                  element={<GoogleDriveViewerPage />}></Route>
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<DataNotFoundAlert />} />
        </Route>
      </Route>
    </Routes>
  );
}
